// Third-party modules

import { CurrencyPipe } from '@angular/common';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { LocalStorageModule } from 'angular-2-local-storage';
import { RecaptchaFormsModule, RecaptchaModule, RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { environment } from 'environments/environment';

// Feature Modules

import { CommonUiModule } from 'common-ui/common-ui.module';
import { ApiConfiguration, ApiModule } from '../api';
import { AppConfig, TYTCommonModule } from '../common';
import { UserModule } from '../user';
import { ValidationModule } from '../validation';
import { ApiConfigurationFactory } from './api-config';
import { AppErrorHandler } from './error-handler';

// Components

import { AppComponent } from './app.component';
export { AppComponent };
import { HomeComponent } from './home/home.component';
import { OffersComponent } from './offers/offers.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { SignUpComponent } from './sign-up/sign-up.component';

import { DonationsGoalsComponent } from '@tytapp/app/donations-goals/donations-goals.component';
import { MarkdownToHtmlPipe } from '@tytapp/common/markdown-to-html.pipe';
import { StripeModule } from '@tytapp/stripe';
import { AssumeComponent } from 'app/assume/assume.component';
import { ForgotComponent } from 'app/forgot/forgot.component';
import { RedirectComponent } from 'app/redirect/redirect.component';
import { CancelMembershipRetentionComponent } from './cancel-membership-retention/cancel-membership-retention.component';
import { CancelMembershipSuccessPageComponent } from './cancel-membership-success-page/cancel-membership-success-page.component';
import { CancelMembershipComponent } from './cancel-membership/cancel-membership.component';
import { CartService } from './cart.service';
import { ChangeEmailSuccessPageComponent } from './change-email-success-page/change-email-success-page.component';
import { ChangeMembershipConfirmationComponent } from './change-membership-confirmation/change-membership-confirmation.component';
import { ChangeMembershipSuccessPageComponent } from './change-membership-success-page/change-membership-success-page.component';
import { ChangeMembershipComponent } from './change-membership/change-membership.component';
import { ClassicOffersComponent } from './classic-offers/classic-offers.component';
import { CommunicationPreferenceComponent } from './communication-preference/communication-preference.component';
import { ConfirmationAccountComponent } from './confirmation-account/confirmation-account.component';
import { ConfirmedOffersComponent } from './confirmed-offers/confirmed-offers.component';
import { CoreValuesComponent } from './core-values/core-values.component';
import { CreditCardSelectorComponent } from './credit-card-selector/credit-card-selector.component';
import { FixedPriceOfferComponent } from './fixed-price-offer/fixed-price-offer.component';
import { FormSubmitButtonComponent } from './form-submit-button/form-submit-button.component';
import { GiftRedemptionComponent } from './gift-redemption/gift-redemption.component';
import { GiftsComponent } from './gifts/gifts.component';
import { LoginOffersComponent } from './login-offers/login-offers.component';
import { MembershipFeatureComponent } from './membership-feature/membership-feature.component';
import { MembershipFeaturesComponent } from './membership-features/membership-features.component';
import { ModernOffersComponent } from './modern-offers/modern-offers.component';
import { NavComponent } from './nav/nav.component';
import { NewsletterFormComponent } from './newsletter-form/newsletter-form.component';
import { NoOpComponent } from './no-op.component';
import { OptinsComponent } from '@tytapp/app/optins/optins.component';
import { NoticeTestComponent } from './notice-test/notice-test.component';
import { OfferInlinePricingComponent } from './offer-inline-pricing/offer-inline-pricing.component';
import { OfferTierComponent } from './offer-tier/offer-tier.component';
import { OffersWizardHostComponent } from './offer-wizard-host/offer-wizard-host.component';
import { FloatingOfferSetTemplateComponent } from './offers-templates/floating-offer-set-template/floating-offer-set-template.component';
import { OffersWizardComponent } from './offers-wizard/offers-wizard.component';
import { OneTimeContributionComponent } from './one-time-contribution/one-time-contribution.component';
import { OneTimeOfferNoticeComponent } from './one-time-offer-notice/one-time-offer-notice.component';
import { PaymentOffersStepComponent } from './payment-offers-step/payment-offers-step.component';
import { PaymentComponent } from './payment/payment.component';
import { PlusTagComponent } from './plus-tag/plus-tag.component';
import { PricingOffersStepComponent } from './pricing-offers-step/pricing-offers-step.component';
import { PricingPromptOffersComponent } from './pricing-prompt-offers/pricing-prompt-offers.component';
import { PrivacyComponent } from './privacy/privacy.component';
import { ROUTES, RoutingProviders } from './routes';
import { SettingsComponent } from './settings/settings.component';
import { SignupOffersStepComponent } from './signup-offers-step/signup-offers-step.component';
import { SocialButtonsComponent } from './social-buttons/social-buttons.component';
import { SponsorshipViewComponent } from './sponsorship-view/sponsorship-view.component';
import { SponsorshipsComponent } from './sponsorships/sponsorships.component';
import { SsoComponent } from './sso/sso.component';
import { TermsComponent } from './terms/terms.component';
import { TestDialogComponent } from './test-dialog/test-dialog.component';
import { UpdateBillingSuccessPageComponent } from './update-billing-success-page/update-billing-success-page.component';
import { UpdateBillingComponent } from './update-billing/update-billing.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from './material.module';
import { MembershipPogBarComponent } from '@tytapp/app/membership-pog-bar/membership-pog-bar.component';
import { ContributionPogBarComponent } from '@tytapp/app/contribution-pog-bar/contribution-pog-bar.component';
import { PageShellComponent } from './page-shell/page-shell.component';
import { BounceToAppComponent } from './bounce-to-app.component';

@NgModule({
    declarations: [
        AppComponent,
        PageShellComponent,
        HomeComponent,
        NavComponent,
        PageNotFoundComponent,
        RedirectComponent,
        OffersComponent,
        SignUpComponent,
        GiftsComponent,
        SponsorshipsComponent,
        PaymentComponent,
        ConfirmationAccountComponent,
        CancelMembershipComponent,
        UpdateBillingComponent,
        TestDialogComponent,
        NoticeTestComponent,
        TermsComponent,
        PrivacyComponent,
        ForgotComponent,
        AssumeComponent,
        ChangeMembershipComponent,
        FormSubmitButtonComponent,
        PlusTagComponent,
        CommunicationPreferenceComponent,
        SsoComponent,
        CreditCardSelectorComponent,
        GiftRedemptionComponent,
        SponsorshipsComponent,
        SponsorshipViewComponent,
        NewsletterFormComponent,
        UpdateBillingSuccessPageComponent,
        CancelMembershipSuccessPageComponent,
        ChangeEmailSuccessPageComponent,
        ChangeMembershipSuccessPageComponent,
        BounceToAppComponent,
        SettingsComponent,
        ClassicOffersComponent,
        ModernOffersComponent,
        PricingOffersStepComponent,
        DonationsGoalsComponent,
        SignupOffersStepComponent,
        PaymentOffersStepComponent,
        FixedPriceOfferComponent,
        PricingPromptOffersComponent,
        ConfirmedOffersComponent,
        LoginOffersComponent,
        SocialButtonsComponent,
        OneTimeOfferNoticeComponent,
        CoreValuesComponent,
        OneTimeContributionComponent,
        OffersWizardComponent,
        CancelMembershipRetentionComponent,
        OfferInlinePricingComponent,
        OfferTierComponent,
        ChangeMembershipConfirmationComponent,
        MembershipFeatureComponent,
        MembershipFeaturesComponent,
        FloatingOfferSetTemplateComponent,
        OffersWizardHostComponent,
        NoOpComponent,
        OptinsComponent,
        MarkdownToHtmlPipe,
        MembershipPogBarComponent,
        ContributionPogBarComponent
    ],
    imports: [
        BrowserModule.withServerTransition({
            appId: 'tytapp'
        }),
        BrowserAnimationsModule,
        FormsModule,
        StripeModule.forRoot(),
        RouterModule.forRoot(ROUTES, { relativeLinkResolution: 'legacy' }),
        LocalStorageModule.forRoot({
            prefix: 'tyt',
            storageType: 'localStorage'
        }),
        StripeModule.forRoot(),
        MaterialModule.forRoot(),

        // Feature Modules
        TYTCommonModule.forRoot(),
        ApiModule.forRoot(),
        UserModule.forRoot(),
        ValidationModule.forRoot(),
        CommonUiModule,
        RecaptchaModule,
        RecaptchaFormsModule,
        RecaptchaV3Module
    ],
    providers: [
        ...RoutingProviders,
        CartService,
        { provide: ApiConfiguration,
            useFactory: ApiConfigurationFactory,
            deps: [AppConfig] },
        { provide: ErrorHandler,
            useClass: AppErrorHandler },
        CurrencyPipe,
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaV3SiteKey },
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
